import { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = '/api';

export const useMovements = () => {
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showMovementForm, setShowMovementForm] = useState(false);
  const [newMovement, setNewMovement] = useState({ 
    date: new Date().toISOString().slice(0, 10), 
    type: 'venta', 
    amount: 0, 
    clientId: 1, 
    serviceId: 1, 
    description: '' 
  });
  const [monthFilter, setMonthFilter] = useState('');
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear().toString());
  
  const { csrfToken, refreshCsrfToken } = useAuth();

  const ensureFreshCsrfToken = async () => {
    try {
      const result = await refreshCsrfToken();
      if (!result.success) {
        console.error('Error al refrescar el token CSRF');
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error al refrescar el token CSRF:', error);
      return false;
    }
  };

  const fetchMovements = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/movements`);
      setMovements(response.data);
      setError(null);
    } catch (err) {
      console.error('Error:', err);
      setError('Error al cargar los movimientos. Por favor, intente de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMovement = async () => {
    try {
      setLoading(true);
      
      if (!await ensureFreshCsrfToken()) {
        setError('Error al obtener un token CSRF válido. Intente recargar la página.');
        setLoading(false);
        return;
      }
      
      const movementData = {
        date: newMovement.date,
        type: newMovement.type,
        amount: parseFloat(newMovement.amount),
        clientId: parseInt(newMovement.clientId),
        serviceId: parseInt(newMovement.serviceId),
        description: newMovement.description
      };

      if (newMovement.id) {
        await axios.put(`${API_URL}/movements/${newMovement.id}`, movementData, {
          headers: {
            'X-CSRF-Token': csrfToken
          }
        });
      } else {
        await axios.post(`${API_URL}/movements`, movementData, {
          headers: {
            'X-CSRF-Token': csrfToken
          }
        });
      }

      await fetchMovements();
      
      setNewMovement({ 
        date: new Date().toISOString().slice(0, 10), 
        type: 'venta', 
        amount: 0, 
        clientId: '', 
        serviceId: '', 
        description: '' 
      });
      setShowMovementForm(false);
      setError(null);
    } catch (err) {
      console.error('Error:', err);
      setError('Error al guardar el movimiento. Por favor, intente de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMovement = async (movementId) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este movimiento?')) {
      try {
        setLoading(true);
        
        if (!await ensureFreshCsrfToken()) {
          setError('Error al obtener un token CSRF válido. Intente recargar la página.');
          setLoading(false);
          return;
        }
        
        await axios.delete(`${API_URL}/movements/${movementId}`, {
          headers: {
            'X-CSRF-Token': csrfToken
          }
        });

        await fetchMovements();
        setError(null);
      } catch (err) {
        console.error('Error:', err);
        setError('Error al eliminar el movimiento. Por favor, intente de nuevo.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEditMovement = (movement) => {
    setNewMovement({
      id: movement.id,
      date: new Date(movement.date).toISOString().slice(0, 10),
      type: movement.type,
      amount: movement.amount,
      clientId: movement.client_id || movement.clientId,
      serviceId: movement.service_id || movement.serviceId,
      description: movement.description
    });
    setShowMovementForm(true);
  };

  const getFilteredMovements = () => {
    if (!monthFilter && !yearFilter) return movements;
    
    return movements.filter(movement => {
      const date = new Date(movement.date);
      
      let matchesFilter = true;
      
      if (monthFilter) {
        matchesFilter = matchesFilter && (date.getMonth() + 1 === parseInt(monthFilter));
      }
      
      if (yearFilter) {
        matchesFilter = matchesFilter && (date.getFullYear() === parseInt(yearFilter));
      }
      
      return matchesFilter;
    });
  };

  const calculateSalesStats = (services, clients, getServiceName, getClientName) => {
    const filteredMovements = getFilteredMovements();
    
    const stats = {
      totalSales: 0,
      totalIncome: 0,
      avgTicket: 0,
      salesByService: {},
      salesByClient: {}
    };
    
    filteredMovements.forEach(movement => {
      if (movement.type === 'venta') {
        const amount = parseFloat(movement.amount);
        stats.totalSales++;
        stats.totalIncome += amount;
        
        // Agrupar por servicio
        const serviceId = movement.service_id || movement.serviceId;
        const serviceName = getServiceName(serviceId);
        
        if (!stats.salesByService[serviceName]) {
          stats.salesByService[serviceName] = {
            count: 0,
            amount: 0
          };
        }
        
        stats.salesByService[serviceName].count++;
        stats.salesByService[serviceName].amount += amount;
        
        // Agrupar por cliente
        const clientId = movement.client_id || movement.clientId;
        const clientName = getClientName(clientId);
        
        if (!stats.salesByClient[clientName]) {
          stats.salesByClient[clientName] = {
            count: 0,
            amount: 0
          };
        }
        
        stats.salesByClient[clientName].count++;
        stats.salesByClient[clientName].amount += amount;
      }
    });
    
    // Calcular ticket promedio
    if (stats.totalSales > 0) {
      stats.avgTicket = stats.totalIncome / stats.totalSales;
    }
    
    return stats;
  };

  const calculateTotalSales = () => {
    return movements
      .filter(movement => movement.type === 'venta')
      .reduce((total, movement) => total + parseFloat(movement.amount), 0);
  };

  return {
    movements,
    loading,
    error,
    setError,
    showMovementForm,
    setShowMovementForm,
    newMovement,
    setNewMovement,
    monthFilter,
    setMonthFilter,
    yearFilter,
    setYearFilter,
    fetchMovements,
    handleSaveMovement,
    handleDeleteMovement,
    handleEditMovement,
    getFilteredMovements,
    calculateSalesStats,
    calculateTotalSales
  };
};
